/**
 * Formats a failed Submit Session error into a message that surfaces the real
 * server cause instead of a generic "Failed to submit session." (#30610).
 *
 * The submit handler previously swallowed the HttpErrorResponse, hiding the
 * actual 4xx/5xx status and server validation text from the counter and the
 * logs. This extracts, in order of preference: a string error body, an object
 * error body's `message`, and finally the HTTP status — always returning a
 * non-empty, `undefined`-free message.
 */
export function formatSubmitSessionError(err: unknown): string {
  const generic = 'Failed to submit session.';
  const e = err as
    | { status?: number; error?: unknown; message?: string }
    | null
    | undefined;

  if (!e || typeof e !== 'object') return generic;

  const serverMessage =
    extractServerMessage(e.error) ?? extractServerMessage(e.message);

  if (serverMessage) {
    return e.status ? `${serverMessage} (${e.status})` : serverMessage;
  }

  if (typeof e.status === 'number' && e.status > 0) {
    return `${generic} (HTTP ${e.status})`;
  }

  return generic;
}

function extractServerMessage(body: unknown): string | null {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (body && typeof body === 'object') {
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message.trim();
    }
  }
  return null;
}
